import React from 'react';

type UseDropdownMaxHeightParams = {
  defaultHeight: number;
  minHeight?: number;
  padding?: number;
  side: 'top' | 'bottom';
};

export const useFitDropdown = ({
  defaultHeight,
  minHeight = 0,
  padding = 32,
  side,
}: UseDropdownMaxHeightParams) => {
  const ref = React.useRef<HTMLElement | null | undefined>();
  const [height, setHeight] = React.useState(defaultHeight);
  const [width, setWidth] = React.useState<number>(0);

  const updateHeight = React.useCallback(() => {
    let newHeight = window.innerHeight;
    const rect = ref.current?.getBoundingClientRect();

    if (rect) {
      newHeight = side === 'top' ? rect.y : window.innerHeight - (rect.bottom + window.scrollY);
    }

    setHeight(Math.min(defaultHeight, Math.max(minHeight, newHeight) - padding));
  }, [defaultHeight, minHeight, padding, side]);

  const updateWidth = React.useCallback(() => {
    setWidth(ref.current?.getBoundingClientRect()?.width ?? 0);
  }, []);

  const updateWidthAndHeight = React.useCallback(() => {
    updateWidth();
    updateHeight();
  }, [updateHeight, updateWidth]);

  const removeListeners = React.useCallback(() => {
    window.removeEventListener('resize', updateWidthAndHeight, true);
    document.removeEventListener('scroll', updateHeight, true);
  }, [updateHeight, updateWidthAndHeight]);

  React.useEffect(() => {
    return () => {
      removeListeners();
      ref.current = undefined;
    };
  }, [removeListeners]);

  const targetRef = React.useCallback(
    (node?: HTMLElement | null) => {
      if (ref.current) {
        removeListeners();
      }

      if (node) {
        window.addEventListener('resize', updateWidthAndHeight, true);
        document.addEventListener('scroll', updateHeight, true);
      }

      ref.current = node;

      if (ref.current) {
        updateWidthAndHeight();
      }
    },
    [removeListeners, updateHeight, updateWidthAndHeight],
  );

  return {
    targetRef,
    height,
    width,
  };
};
